import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import DocumentModal from '../common/DocumentModal.js';
import onhireService from '../../services/OnHireService.js';
import {
  Package, Pencil, ArrowLeft,
  ChevronDown, ChevronUp,
  FolderOpen, FileDown, Loader2,
  ImageIcon, Info, Database, Settings, ShieldAlert, ZapOff, Puzzle,
  Calendar, Download, DollarSign, CheckCircle, XCircle
} from 'lucide-react';
import OnHireFileCabinet from './OnHireFileCabinet.js';
import { ROLES } from '../../utils/permissions.js';
import { getViewPageActionButtons, isViewPageActionDisabled } from '../../utils/roleBasedUIHelpers.js';


const rv = (value) => (!value ? 'N/A' : value);

const fmtDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-IN', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
};


/** Collapsible section header that mirrors the AddOnHire accordion style */
const SectionHeader = ({ icon: Icon, label, sectionKey, expanded, onToggle, colorClass = 'bg-gray-100 text-gray-700' }) => (
  <button
    type="button"
    onClick={() => onToggle(sectionKey)}
    className={`
      w-full px-4 sm:px-6 py-3 sm:py-4
      ${colorClass}
      font-semibold text-left
      flex items-center justify-between
      border-b border-gray-100
      hover:opacity-90
      transition-opacity duration-200
    `}
  >
    <span className="flex items-center gap-2">
      {Icon && <Icon className="w-5 h-5 text-buttonBg" />}
      {label}
    </span>
    {expanded
      ? <ChevronUp className="w-5 h-5 text-buttonBg" />
      : <ChevronDown className="w-5 h-5 text-buttonBg" />}
  </button>
);

/** Read-only field – label + value pair used across every section */
const Field = ({ label, value, colSpan = '' }) => (
  <div className={colSpan}>
    <p className="text-xs sm:text-sm font-medium text-gray-500 mb-0.5">{label}</p>
    <p className="text-sm sm:text-base text-secondary font-medium">{rv(value)}</p>
  </div>
);

/** Date field (uses fmtDate) */
const DateField = ({ label, value, colSpan = '' }) => (
  <div className={`flex flex-col ${colSpan}`}>
    <p className="text-xs sm:text-sm font-medium text-gray-500 mb-0.5 flex items-center gap-1">
      <Calendar className="w-3.5 h-3.5 text-buttonBg" /> {label}
    </p>
    <p className="text-sm sm:text-base text-secondary font-medium">{fmtDate(value)}</p>
  </div>
);

/** Plain string field – for MM/YYYY and "MM/YYYY     By: Name" formats */
const StringField = ({ label, value, colSpan = '' }) => (
  <div className={`flex flex-col ${colSpan}`}>
    <p className="text-xs sm:text-sm font-medium text-gray-500 mb-0.5 flex items-center gap-1">
      <Calendar className="w-3.5 h-3.5 text-buttonBg" /> {label}
    </p>
    <p className="text-sm sm:text-base text-secondary font-medium">{value || 'N/A'}</p>
  </div>
);

/** Yes / No badge */
const YesNoBadge = ({ value }) => {
  const yes = String(value).toLowerCase() === 'yes';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold
      ${yes ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
      {rv(value)}
    </span>
  );
};

const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'paid': {
        bg: 'bg-green-100',
        text: 'text-green-700',
        label: 'Paid',
        icon: CheckCircle
      },
      'unpaid': {
        bg: 'bg-red-100',
        text: 'text-red-700',
        label: 'Unpaid',
        icon: XCircle
      },
      'pending': {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        label: 'Payment Pending',
        icon: DollarSign
      }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;

    return (
      <span className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

/** Sub-group heading inside expanded section (mirrors AddOnHire h4 style) */
const SubHeading = ({ label }) => (
  <h4 className="font-semibold text-buttonBg text-base mb-4 border-b pb-2">{label}</h4>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const ViewOnHire = () => {
  const { onhireId } = useParams();
  const [onhire, setOnhire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showFileCabinet, setShowFileCabinet] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [currentImageType, setCurrentImageType] = useState(null);

  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    images: true,
    tankDetails: true,
    valves: false,
    srv: false,
    ruptureDisc: false,
    ancillary: false,
  });

  const navigate = useNavigate();
  const { user } = useAuth();

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => { fetchOnhire(); }, [onhireId]);

  const fetchOnhire = async () => {
    try {
      const response = await onhireService.getById(onhireId);
      setOnhire(response.data);
    } catch (err) {
      Alert.error('Failed to load onhire details');
      console.error(err);
      navigate('/onhires');
    } finally {
      setLoading(false);
    }
  };

  // ── Accordion ──────────────────────────────────────────────────────────────
  const toggleSection = (section) =>
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));

  // ── Images ─────────────────────────────────────────────────────────────────
  const getImageUrl = (imageType) => {
    if (imageType === 'image_1') {
      if (onhire.image_1_data) return `data:image/jpeg;base64,${onhire.image_1_data}`;
      if (onhire.has_image_1)  return onhireService.getImage1(onhire.id);
    }
    if (imageType === 'image_2') {
      if (onhire.image_2_data) return `data:image/jpeg;base64,${onhire.image_2_data}`;
      if (onhire.has_image_2)  return onhireService.getImage2(onhire.id);
    }
    return null;
  };

  const openImageModal = (imageType) => {
    setCurrentImage(getImageUrl(imageType));
    setCurrentImageType(imageType === 'image_1' ? 'Frame / Cladding Diagram (Image 1)' : 'Tank Mapping Chart (Image 2)');
    setIsDocModalOpen(true);
  };

  // ── PDF Download ───────────────────────────────────────────────────────────
  const handleDownloadReport = async () => {
    setDownloadingPdf(true);
    try {
      const blob = await onhireService.downloadReport(onhire.id);
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href  = url;
      const safeId = (onhire.tank_no || onhire.onhire_id).replace(/\//g, '_');
      link.download = `OnHire_Report_${safeId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success('Report downloaded successfully!');
    } catch (err) {
      Alert.error('Failed to download report. Please try again.');
      console.error(err);
    } finally {
      setDownloadingPdf(false);
    }
  };

  // ── Roles ──────────────────────────────────────────────────────────────────
  const userRole = user?.role_name || user?.role;
  const isClient = userRole === ROLES.CLIENT;
  const actionButtons = getViewPageActionButtons(userRole);
  const isActionDisabled = (action) => isViewPageActionDisabled(userRole, action);

  // ── Loading / guard ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="max-w-[1850px] mx-auto">
        <Header />
        <div className="flex">
          <Sidebar />
          <div className="flex bg-light w-full mt-16 sm:mt-0">
            <div className="flex-1 flex items-center justify-center min-h-screen">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4" />
                <p className="text-sm sm:text-base text-gray-600">Loading onhire details…</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!onhire) return null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-6">
              <div className="max-w-8xl mx-auto">

                {/* ── Page header ─────────────────────────────────────────── */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="flex items-center gap-3">
                    <h1 className="text-xl sm:text-2xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <Package className="w-6 h-6 text-buttonBg" />
                      View OnHire: <span className="text-buttonBg">{onhire.onhire_id}</span>
                    </h1>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2">
                    {/* File Cabinet */}
                    <button
                      onClick={() => setShowFileCabinet(true)}
                      className="
                        relative overflow-hidden
                        group flex items-center justify-center gap-2
                        px-3 sm:px-5 py-2.5 sm:py-2
                        text-xs sm:text-sm
                        rounded-lg
                        border border-buttonBg
                        bg-buttonBg text-buttonTxt
                        font-medium whitespace-nowrap
                        transition-colors duration-300 ease-out
                        hover:bg-transparent hover:text-buttonBg
                        disabled:opacity-60 disabled:cursor-not-allowed
                      "
                    >
                      <span className="
                        absolute inset-0 bg-buttonBg scale-x-100
                        group-hover:scale-x-0 origin-center
                        transition-transform duration-300 ease-out z-0
                      " />
                      <span className="relative z-10 flex items-center gap-2">
                        <FolderOpen className="w-4 h-4" /> File Cabinet
                      </span>
                    </button>

                    {/* Download Report */}
                    <button
                      onClick={handleDownloadReport}
                      disabled={downloadingPdf || (isClient && !onhire.published)}
                      title={isClient && !onhire.published ? 'PDF download is only available after publishing' : ''}
                      className={`relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-white text-buttonBg font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:text-white ${(isClient && !onhire.published) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className="
                        absolute inset-0 bg-buttonBg scale-x-0
                        group-hover:scale-x-100 origin-center
                        transition-transform duration-300 ease-out z-0
                      " />
                      <span className="relative z-10 flex items-center gap-2">
                        {downloadingPdf
                          ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                          : <><Download className="w-4 h-4" /> Download Report</>}
                      </span>
                    </button>

                    <button
                    onClick={() => navigate('/onhires')}
                    className="
                      relative overflow-hidden
                      group flex items-center justify-center gap-2
                      px-3 sm:px-5 py-2.5 sm:py-2
                      text-xs sm:text-sm rounded-lg
                      border border-buttonBg bg-white text-buttonBg
                      font-medium whitespace-nowrap
                      transition-colors duration-300 ease-out hover:text-white
                    "
                  >
                    <span className="
                      absolute inset-0 bg-buttonBg scale-x-0
                      group-hover:scale-x-100 origin-center
                      transition-transform duration-300 ease-out z-0
                    " />
                    <span className="relative z-10 flex items-center gap-2">
                      <ArrowLeft className="w-4 h-4" /> Back to List
                    </span>
                  </button>

                    {/* Edit */}
                    {actionButtons.edit && (
                      <button
                        onClick={() => navigate(`/onhires/edit/${onhire.id}`)}
                        disabled={isActionDisabled('edit')}
                        className={`relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-white text-buttonBg font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:text-white ${isActionDisabled('edit') ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span className="absolute inset-0 bg-buttonBg scale-x-0 group-hover:scale-x-100 origin-center transition-transform duration-300 ease-out z-0" />
                        <span className="relative z-10 flex items-center gap-2">
                          <Pencil className="w-4 h-4" /> Edit OnHire
                        </span>
                      </button>
                    )}
                  </div>
                </div>

                {/* ── Sections ────────────────────────────────────────────── */}
                <div className="bg-white rounded-sm shadow-sm space-y-6 p-4 sm:p-8">

                  {/* ── TANK INSPECTION IMAGES ─────────────────────────────── */}
                  <div className="border border-gray-200 rounded-sm">
                    <SectionHeader
                      icon={ImageIcon}
                      label="Tank Inspection Images"
                      sectionKey="images"
                      expanded={expandedSections.images}
                      onToggle={toggleSection}
                    />
                    {expandedSections.images && (
                      <div className="p-4 sm:p-6 border-t border-gray-200 space-y-4">
                        <p className="text-sm text-gray-600">
                          Inspection diagrams saved with this on-hire record.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* Image 1 */}
                          <div className="border border-gray-300 rounded-lg p-4 bg-white">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-secondary">Frame / Cladding Diagram</h4>
                                <p className="text-xs text-gray-500 mt-1">Image 1 – Tank front &amp; rear views</p>
                              </div>
                              {onhire.has_image_1 ? (
                                <button
                                  onClick={() => openImageModal('image_1')}
                                  className="flex items-center gap-2 px-4 py-2 bg-buttonBg hover:bg-buttonBg text-white rounded-lg font-medium text-sm transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  View
                                </button>
                              ) : (
                                <span className="text-sm text-gray-400 italic">No image</span>
                              )}
                            </div>
                          </div>

                          {/* Image 2 */}
                          <div className="border border-gray-300 rounded-lg p-4 bg-white">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-secondary">Tank Mapping Chart</h4>
                                <p className="text-xs text-gray-500 mt-1">Image 2 – Liquid tank section views</p>
                              </div>
                              {onhire.has_image_2 ? (
                                <button
                                  onClick={() => openImageModal('image_2')}
                                  className="flex items-center gap-2 px-4 py-2 bg-buttonBg hover:bg-buttonBg text-white rounded-lg font-medium text-sm transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  View
                                </button>
                              ) : (
                                <span className="text-sm text-gray-400 italic">No image</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── BASIC INFORMATION ──────────────────────────────────── */}
                  <div className="border border-gray-200 rounded-sm">
                    <SectionHeader
                      icon={Info}
                      label="Basic Information"
                      sectionKey="basic"
                      expanded={expandedSections.basic}
                      onToggle={toggleSection}
                    />
                    {expandedSections.basic && (
                      <div className="p-4 sm:p-6 border-t border-gray-200">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                          <Field label="OnHire ID"   value={onhire.onhire_id} />
                          <Field label="Client ID"   value={onhire.client_id} />
                          <Field label="Tank Number" value={onhire.tank_no} />
                          <Field label="Location"
                            value={onhire.location
                              ? `${onhire.location.city}, ${onhire.location.state} (${onhire.location.city_code})`
                              : null}
                          />
                          <DateField label="Date of Survey" value={onhire.date_of_survey} />
                          <Field label="Inspector"         value={onhire.inspected_by_name} />
                          <Field label="Form of Liquid Tank" value={onhire.form_of_liquid_tank} />
                          <Field label="Survey Type" value={onhire.survey_type} />

                          {/* Status badges */}
                          <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Status</p>
                            <div className="flex flex-wrap gap-2">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold
                                ${onhire.published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                {onhire.published ? '● Published' : '● Draft'}
                              </span>
                              <div className="flex flex-wrap gap-2">
                                {onhire.send_to_dm && (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                                    Sent to DM
                                  </span>
                                )}
                                {onhire.published && (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                    Published
                                  </span>
                                )}
                                {onhire.payment_status && getPaymentStatusBadge(onhire.payment_status)}
                              </div>
                            </div>
                          </div>

                          <div className="sm:col-span-2">
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Comments</p>
                            <p className="text-sm sm:text-base text-secondary whitespace-pre-wrap">{rv(onhire.comments)}</p>
                          </div>
                          <div className="sm:col-span-2">
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Remarks</p>
                            <p className="text-sm sm:text-base text-secondary whitespace-pre-wrap">{rv(onhire.remarks)}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── TANK DETAILS ───────────────────────────────────────── */}
                  <div className="border border-gray-200 rounded-sm">
                    <SectionHeader
                      icon={Database}
                      label="Tank Details"
                      sectionKey="tankDetails"
                      expanded={expandedSections.tankDetails}
                      onToggle={toggleSection}
                    />
                    {expandedSections.tankDetails && (
                      <div className="p-4 sm:p-6 border-t border-gray-200">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                          <Field label="Tank No"              value={onhire.tank_no} />
                          <Field label="ISO Type"             value={onhire.iso_type} />
                          <Field label="Tank Type"            value={onhire.tank_type} />
                          {/* <Field label="IMO Class"            value={onhire.imo_class} /> */}
                          <Field label="Manufacturer"         value={onhire.manufacturer} />
                          {/* <DateField label="Manufacture Date" value={onhire.manufacture_date} /> */}
                          {/* <Field label="UN Number"            value={onhire.un_number} /> */}
                          <Field label="Serial Number"        value={onhire.serial_number} />
                          {/* <Field label="CSC"                  value={onhire.csc} /> */}
                          {/* <Field label="Cleaning Corporation" value={onhire.cleaning_corporation} /> */}
                          <Field label="Depot Name"           value={onhire.depot_name} />
                          <Field label="Maximum Gross Weight (Kg)" value={onhire.maximum_gross_weight} />
                          <Field label="Tare Weight (kg)"          value={onhire.tare_weight} />
                          <Field label="Capacity (LTR)"       value={onhire.capacity_ltr} />
                          {/* <Field label="Size (Feet)"                 value={onhire.size} /> */}
                          <Field label="MAWP (Bar)"                 value={onhire.mawp} />
                          <Field label="Test Pressure (Bar)"        value={onhire.test_pressure} />
                          <Field label="Steam Tube WP (Bar)"        value={onhire.steam_tube_wp} />
                          <Field label="Steam Test Pressure (Bar)"  value={onhire.steam_test_pressure} />
                          <Field label="Shell Material"       value={onhire.shell_material} />
                          <Field label="Shell Thickness"      value={onhire.shell_thikness} />
                          <Field label="Head Material"        value={onhire.head_material} />
                          <Field label="Head Thickness"       value={onhire.head_thikness} />
                          {/* <DateField label="LED"              value={onhire.led} /> */}
                          {/* <DateField label="NED"              value={onhire.ned} /> */}
                          <StringField label="First Test Date"          value={onhire.first_test_date} />
                          <StringField label="Next Test Date"           value={onhire.next_test_date} />
                          <StringField label="Last 2.5 Year Test Date"  value={onhire.last_2_5_year_test_date} />
                          <StringField label="Last 5 Year Test Date"    value={onhire.last_5_year_test_date} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── VALVES ─────────────────────────────────────────────── */}
                  <div className="border border-gray-200 rounded-sm">
                    <SectionHeader
                      icon={Settings}
                      label="Valves"
                      sectionKey="valves"
                      expanded={expandedSections.valves}
                      onToggle={toggleSection}
                    />
                    {expandedSections.valves && (
                      <div className="p-4 sm:p-6 border-t border-gray-200 space-y-6">

                        {/* Bottom Discharge */}
                        <div>
                          <SubHeading label="Bottom Discharge" />
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            <Field label="Bottom Discharge"       value={onhire.bottom_discharge_fort_valve} />
                            <Field label="Bottom Discharge Serial No."       value={onhire.bottom_discharge_sn} />
                            <Field label="Valve Type"  value={onhire.bottom_discharge_butterfly_valve} />
                            <Field label="Bottom Discharge Spacers"          value={onhire.bottom_discharge_spacers} />
                            <div>
                              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">TIR Strips</p>
                              <YesNoBadge value={onhire.bottom_discharge_tir_strips} />
                            </div>
                          </div>
                        </div>

                        <div className="border-t pt-6">
                          <SubHeading label="Foot Valve" />
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            <Field label="Foot Valve" value={onhire.footvalve_fort_valve} />
                            <Field label="Footvalve Type"         value={onhire.bottom_discharge_highlift} />
                            <div>
                              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Footvalve TIR Strip</p>
                              <YesNoBadge value={onhire.footvalve_tir_strip} />
                            </div>
                          </div>
                        </div>

                        <div className="border-t pt-6">
                          <SubHeading label="Bottom Outlet" />
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            <Field label="Bottom Outlet Connection Type" value={onhire.bottom_outlet_connection_type} />
                            <Field label="Bottom Outlet Cap / Blank" value={onhire.bottom_outlet_cap_blank} />
                            <Field label="Bottom Outlet Cap"         value={onhire.bottom_outlet_cap} />
                            <Field label="Bottom Outlet Remote Trip" value={onhire.remote_trip} />
                          </div>
                        </div>

                        <div className="border-t pt-6">
                          <SubHeading label="Top Discharge" />
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            <Field label="Top Discharge"             value={onhire.top_discharge} />
                            <Field label="Top Discharge Manufacture" value={onhire.top_discharge_manufacture} />
                            <Field label="Top Discharge Type"        value={onhire.top_discharge_type} />
                            <Field label="Siphon Tube"               value={onhire.siphon_tube} />
                            <div>
                              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">TIR Strip</p>
                              <YesNoBadge value={onhire.top_discharge_tir_strip} />
                            </div>
                          </div>
                        </div>

                        <div className="border-t pt-6">
                          <SubHeading label="Top Loading" />
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            <Field label="Top Loading"              value={onhire.top_loading} />
                            <Field label="Top Loading Type"         value={onhire.top_loading_type} />
                            <Field label="Top Loading Manufacture"  value={onhire.top_loading_manufacture} />
                            <Field label="Top Loading Flanged"      value={onhire.top_loading_flanged} />
                            <div>
                              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Top Loading TIR Strips</p>
                              <YesNoBadge value={onhire.top_loading_tir_strips} />
                            </div>
                          </div>
                        </div>

                        <div className="border-t pt-6">
                          <SubHeading label="Air Vent" />
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            <Field label="Air Vent"              value={onhire.air_vent_type} />
                            <Field label="Air Vent Connection"        value={onhire.air_vent_connection} />
                            <div>
                              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Air Vent TIR Strips</p>
                              <YesNoBadge value={onhire.air_vent_connection_tir_strips} />
                            </div>
                            <Field label="Air Vent Cap / Blank"            value={onhire.air_vent_cap_blank} />
                            <Field label="Airline Pressure Gauge" value={onhire.airline_pressure_gauge} />
                          </div>
                        </div>

                      </div>
                    )}
                  </div>

                  {/* ── SRV ────────────────────────────────────────────────── */}
                  <div className="border border-gray-200 rounded-sm">
                    <SectionHeader
                      icon={ShieldAlert}
                      label="Safety Relief Valves (SRV)"
                      sectionKey="srv"
                      expanded={expandedSections.srv}
                      onToggle={toggleSection}
                    />
                    {expandedSections.srv && (
                      <div className="p-4 sm:p-6 border-t border-gray-200 space-y-6">
                        <div>
                          <SubHeading label="SRV 1" />
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            <Field label="SRV 1"      value={onhire.srv1_type} />
                            <Field label="Serial No" value={onhire.srv1_serial_no} />
                            <Field label="Vacuum"   value={onhire.srv1_vacuum} />
                            <Field label="Pressure" value={onhire.srv1_pressure} />
                            <div>
                              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">TIR Strips</p>
                              <YesNoBadge value={onhire.srv1_tir_strips} />
                            </div>
                            <div>
                              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Flame Screen</p>
                              <YesNoBadge value={onhire.srv1_flame_screen} />
                            </div>
                          </div>
                        </div>

                        <div className="border-t pt-6">
                          <SubHeading label="SRV 2" />
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            <Field label="SRV 2"        value={onhire.srv2_type} />
                            <Field label="Serial No"   value={onhire.srv2_serial_no} />
                            <Field label="Vacuum"      value={onhire.srv2_vacuum} />
                            <Field label="Pressure"    value={onhire.srv2_pressure} />
                            <div>
                              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">TIR Strips</p>
                              <YesNoBadge value={onhire.srv2_tir_strips} />
                            </div>
                            <div>
                              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Flame Screen</p>
                              <YesNoBadge value={onhire.srv2_flame_screen} />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── RUPTURE DISCS ──────────────────────────────────────── */}
                  <div className="border border-gray-200 rounded-sm">
                    <SectionHeader
                      icon={ZapOff}
                      label="Rupture Discs"
                      sectionKey="ruptureDisc"
                      expanded={expandedSections.ruptureDisc}
                      onToggle={toggleSection}
                    />
                    {expandedSections.ruptureDisc && (
                      <div className="p-4 sm:p-6 border-t border-gray-200 space-y-6">
                        <div>
                          <SubHeading label="Rupture Disc 1" />
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            <Field label="Rupture Disc 1 Manufacturer" value={onhire.rupture_disc1_manufacturer} />
                            <Field label="Rupture Disc 1 Pressure"     value={onhire.rupture_disc1_pressure} />
                            <Field label="Rupture Disc 1 Manometer"    value={onhire.rupture_disc1_manometer} />
                            <Field label="Rupture Disc 1 Size"         value={onhire.rupture_disc1_size} />
                          </div>
                        </div>
                        <div className="border-t pt-6">
                          <SubHeading label="Rupture Disc 2" />
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            <Field label="Rupture Disc 2 Manufacturer" value={onhire.rupture_disc2_manufacturer} />
                            <Field label="Rupture Disc 2 Pressure"     value={onhire.rupture_disc2_pressure} />
                            <Field label="Rupture Disc 2 Manometer"    value={onhire.rupture_disc2_manometer} />
                            <Field label="Rupture Disc 2 Size"         value={onhire.rupture_disc2_size} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── ANCILLARY FITTINGS ─────────────────────────────────── */}
                  <div className="border border-gray-200 rounded-sm">
                    <SectionHeader
                      icon={Puzzle}
                      label="Ancillary Fittings"
                      sectionKey="ancillary"
                      expanded={expandedSections.ancillary}
                      onToggle={toggleSection}
                    />
                    {expandedSections.ancillary && (
                      <div className="p-4 sm:p-6 border-t border-gray-200">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                          <Field label="Cladding Type"             value={onhire.cladding_type} />
                          <Field label="Ladders Type"              value={onhire.ladders_type} />
                          <Field label="Placard Holders"           value={onhire.placard_holders} />
                          <Field label="Decals Type"               value={onhire.decals_type} />
                          <Field label="Temperature Gauge Type"    value={onhire.temp_gauge_type} />
                          <Field label="Temperature Gauge Notes"   value={onhire.temp_gauge_notes} />
                          <Field label="Document Tube"             value={onhire.document_tube} />
                          <Field label="Steam Tube Cap"            value={onhire.steam_tube_cap} />
                          <Field label="Steam Accessories"         value={onhire.steam_accessories} />
                          <Field label="Bottom Compartment Manufacturer"  value={onhire.bottom_compartment_cover} />
                          <Field label="Electrical Heating/Cooling" value={onhire.electrical_heating_cooling} />
                          <Field label="Calibration Table"         value={onhire.calibration_table} />
                          {/* <Field label="Calibration Table Tank No" value={onhire.calibration_table_tank_no} /> */}
                          <Field label="Manlid with Swingbolts"               value={onhire.manlid_type} />
                          <Field label="Collapsable Handrails"     value={onhire.collapsable_handrails} />
                          <Field label="Dipstick"                  value={onhire.dipstick} />
                          <Field label="Topcovers Lockings"        value={onhire.topcovers_lockings} />
                          <Field label="Walkway"              value={onhire.walkway_type} />
                          <Field label="Manlid Gasket"             value={onhire.manlid_gasket} />
                          <Field label="Grounding Lug"             value={onhire.grounding_lug} />
                          <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Bottom Plate</p>
                            <YesNoBadge value={onhire.bottom_plate} />
                          </div>
                          {/* <div className="sm:col-span-3">
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Frame / Cladding Details</p>
                            <p className="text-sm sm:text-base text-secondary whitespace-pre-wrap">
                              {rv(onhire.frame_cladding_details)}
                            </p>
                          </div> */}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── METADATA ───────────────────────────────────────────── */}
                  <div className="p-4 sm:p-6 bg-gray-50 border border-gray-200 rounded-sm">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Record Info</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Field label="Created By"    value={onhire.created_by} />
                      <DateField label="Created"   value={onhire.created_at} />
                      <DateField label="Last Updated" value={onhire.updated_at} />
                    </div>
                  </div>

                </div>{/* /sections wrapper */}

                {/* ── Bottom action bar ────────────────────────────────────── */}
                <div className="flex flex-wrap gap-4 mt-6">

                  {actionButtons.edit && (
                    <button
                      onClick={() => navigate(`/onhires/edit/${onhire.id}`)}
                      disabled={isActionDisabled('edit')}
                      className={`relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-buttonBg text-buttonTxt font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:bg-transparent hover:text-buttonBg ${isActionDisabled('edit') ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className="absolute inset-0 bg-buttonBg scale-x-100 group-hover:scale-x-0 origin-center transition-transform duration-300 ease-out z-0" />
                      <span className="relative z-10 flex items-center gap-2">
                        <Pencil className="w-4 h-4" /> Edit OnHire
                      </span>
                    </button>
                  )}
                </div>

              </div>
            </main>
          </div>
        </div>
      </div>

      {/* ── File Cabinet Modal ──────────────────────────────────────────────── */}
      {showFileCabinet && onhire && (
        <OnHireFileCabinet
          onhireId={onhire.onhire_id}
          clientId={onhire.client_id}
          onClose={() => setShowFileCabinet(false)}
        />
      )}

      {/* ── Image Modal ─────────────────────────────────────────────────────── */}
      <DocumentModal
        isOpen={isDocModalOpen}
        onClose={() => { setIsDocModalOpen(false); setCurrentImage(null); setCurrentImageType(null); }}
        documentUrl={currentImage}
        documentType={currentImageType}
      />
    </div>
  );
};

export default ViewOnHire;